import React, { memo } from '../../lib/teact/teact';
import { withGlobal } from '../../global';

import type { ApiChain } from '../../api/types';
import type { Account } from '../../global/types';
import type { AccountBalance } from '../../hooks/useAccountsBalances';

import {
  selectCurrentAccount,
  selectCurrentAccountChainDisplay,
  selectCurrentAccountId,
} from '../../global/selectors';
import buildClassName from '../../util/buildClassName';
import { getOrderedAccountChains } from '../../util/chain';
import { formatAccountAddresses } from '../../util/formatAccountAddress';
import { formatCurrency } from '../../util/formatNumber';
import isViewAccount from '../../util/isViewAccount';

import SensitiveData from '../ui/SensitiveData';
import WalletAvatar from '../ui/WalletAvatar';

import styles from './AccountInfo.module.scss';

interface StateProps {
  currentAccount?: Account;
  currentAccountId?: string;
  visibleChains?: ApiChain[];
  isSensitiveDataHidden?: boolean;
  isTestnet?: boolean;
}

interface OwnProps {
  balanceData?: AccountBalance;
}

function AccountInfo({
  currentAccount,
  currentAccountId,
  visibleChains,
  isSensitiveDataHidden,
  isTestnet,
  balanceData,
}: StateProps & OwnProps) {
  if (!currentAccount) return;

  const isView = isViewAccount(currentAccount.type);
  const formattedAddress = formatAccountAddresses(
    currentAccount.byChain,
    visibleChains ?? getOrderedAccountChains(currentAccount.byChain),
  );

  return (
    <div className={styles.info}>
      <WalletAvatar
        title={currentAccount.title}
        accountId={currentAccountId}
        className={styles.avatar}
      />

      <div className={styles.titleRow}>
        <span className={styles.title}>{currentAccount.title}</span>

        {balanceData && (
          <SensitiveData
            isActive={isSensitiveDataHidden}
            rows={2}
            min={5}
            max={10}
            seed={currentAccount.title || ''}
            cellSize={8}
            align="right"
          >
            <div className={buildClassName(styles.balance, 'rounded-font')}>
              {formatCurrency(balanceData.value, balanceData.currencySymbol)}
            </div>
          </SensitiveData>
        )}
      </div>

      <div className={styles.address}>
        {isTestnet && <i className={buildClassName(styles.icon, 'icon-testnet')} aria-hidden />}
        {isView && <i className={buildClassName(styles.icon, 'icon-eye-filled')} aria-hidden />}
        {formattedAddress}
      </div>
    </div>
  );
}

export default memo(withGlobal((global): StateProps => {
  const currentAccount = selectCurrentAccount(global);
  const currentAccountId = selectCurrentAccountId(global);
  const { isSensitiveDataHidden, isTestnet } = global.settings;

  return {
    currentAccount,
    currentAccountId,
    visibleChains: selectCurrentAccountChainDisplay(global)?.visibleChains,
    isSensitiveDataHidden,
    isTestnet,
  };
})(AccountInfo));
